import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["playlistId", "showLoader", "appearsIn"];
import { useMemo } from 'react';
import UIRouterButton from 'ui-addon-react-router-dom/UIRouterButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getListingUrl, getPlaylistUrl } from '../../../utils/RoutingUtils';
import { usePlaylistProgress, useStartPlaylistProgress } from '../../userProgress/userProgressQueries';
import { useFullPlaylist, useUserOwnsPlaylist } from '../savedItemsQueries';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIFlex from 'UIComponents/layout/UIFlex';
import styled from 'academy-common-lib/utils/Styling';
import { useLocation } from 'react-router-dom';
import UsageTracker from '../../../utils/UsageTracker';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ButtonLabel = styled(FormattedMessage).withConfig({
  displayName: "StartPlaylistButton__ButtonLabel",
  componentId: "wy0z7e-0"
})(["flex:1;opacity:", ";"], props => props.disabled ? 0.3 : 1);
const useFindNextContent = (playlistId, appearsIn) => {
  const {
    pathname
  } = useLocation();
  const {
    playlistProgress
  } = usePlaylistProgress(playlistId);
  const {
    playlist,
    displayedPlaylistItems
  } = useFullPlaylist(playlistId);
  const isAssignedPlaylist = playlist && playlist.assignments && playlist.assignments.length > 0 || false;
  const nextItem = useMemo(() => {
    const playlistItems = displayedPlaylistItems || [];
    const progressItems = playlistProgress && playlistProgress.itemsProgress || [];
    const inProgressItems = progressItems.filter(item => item.status !== 'COMPLETED');
    if (
    /**
     * There are no in progress items and all items are completed
     */
    inProgressItems.length === 0 && progressItems.length === playlistItems.length) {
      return Object.assign({}, playlistItems[0], {
        isStarted: true
      });
    } else if (
    /**
     * There are no in progress items, some completed items and possibly some
     * not started items we want to look for
     */
    inProgressItems.length === 0 && progressItems.length !== playlistItems.length) {
      const notStartedItem = playlistItems.find(item => !progressItems.find(progressItem => progressItem.itemId === item.itemId && progressItem.type === item.type));
      if (notStartedItem) {
        return Object.assign({}, notStartedItem, {
          isStarted: false
        });
      }
    } else {
      /**
       * There are in progress items and we want to find the most recently progressed item.
       */
      const progressItem = inProgressItems[0];
      const newSelectedItem = playlistItems.find(item => item.itemId === progressItem.itemId && item.type === progressItem.type);
      if (newSelectedItem) {
        return Object.assign({}, newSelectedItem, {
          isStarted: true
        });
      }
    }
    return Object.assign({}, playlistItems[0], {
      isStarted: true
    });
  }, [displayedPlaylistItems, playlistProgress]);
  if (!nextItem || !playlistProgress || !playlist) return null;
  return {
    url: getListingUrl({
      isStarted: nextItem.isStarted,
      type: nextItem.type,
      id: nextItem.itemId,
      language: nextItem.language,
      playlistId,
      isAssignedPlaylist,
      currentPathname: pathname,
      appearsIn
    }).fullUrl,
    language: nextItem.language,
    type: nextItem.type,
    itemId: nextItem.itemId
  };
};
const StartPlaylistButton = _ref => {
  let {
      playlistId,
      showLoader,
      appearsIn
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    displayedPlaylistItems
  } = useFullPlaylist(playlistId);
  const {
    playlistProgress,
    playlistProgressLoading
  } = usePlaylistProgress(playlistId);
  const userIsOwner = useUserOwnsPlaylist(playlistId);
  const nextContent = useFindNextContent(playlistId, appearsIn);
  const [startPlaylistProgress] = useStartPlaylistProgress({
    playlistId,
    language: nextContent && nextContent.language || 'en'
  });
  if (!showLoader && (!playlistProgress || !(nextContent && nextContent.url))) return null;
  let inProgress = false;
  let buttonText = 'learningCenter.savedItems.playlistDetails.startPlaylist';
  if (playlistProgress && playlistProgress.status === 'IN_PROGRESS') {
    inProgress = true;
    buttonText = 'learningCenter.savedItems.playlistDetails.continuePlaylist';
  } else if (playlistProgress && playlistProgress.status === 'COMPLETED') {
    buttonText = 'learningCenter.savedItems.playlistDetails.completedPlaylist';
  }
  const isLoading = playlistProgressLoading;
  const isDisabled = isLoading || !(displayedPlaylistItems !== null && displayedPlaylistItems !== void 0 && displayedPlaylistItems.length);
  const trackPlaylistStart = () => {
    if (!inProgress && nextContent) {
      startPlaylistProgress().catch(e => {
        Raven.captureException(e);
      });
    }
    UsageTracker.track('playlistInteraction', {
      action: inProgress ? 'continue-playlist' : 'start-playlist',
      isOwner: userIsOwner,
      playlistId
    });
  };
  return /*#__PURE__*/_jsx(UIRouterButton, Object.assign({
    disabled: isDisabled,
    onClick: trackPlaylistStart,
    to: nextContent && nextContent.url || getPlaylistUrl(playlistId),
    use: "primary"
  }, props, {
    children: /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      justify: "center",
      children: [/*#__PURE__*/_jsx(ButtonLabel, {
        disabled: isLoading || props.use === 'unstyled' && isDisabled ? 1 : 0,
        message: buttonText
      }), showLoader && isLoading && /*#__PURE__*/_jsx(UILoadingSpinner, {
        className: "loading-spinner",
        size: "extra-small",
        style: {
          margin: 0,
          position: 'absolute'
        }
      })]
    })
  }));
};
export default StartPlaylistButton;